import type { CourseLearning } from '../types/learning'

export const courseLearning: CourseLearning[] = [
  {
    courseCode: 'SYE 311',
    topics: [
      { id: 't-1', label: 'Linear programming — formulation', complete: true },
      { id: 't-2', label: 'Simplex method', complete: true },
      { id: 't-3', label: 'Duality and sensitivity', complete: true },
      { id: 't-4', label: 'Transportation and assignment', complete: true },
      { id: 't-5', label: 'Network models', complete: true },
      { id: 't-6', label: 'Integer programming', complete: true },
      { id: 't-7', label: 'Queueing theory', complete: true },
      { id: 't-8', label: 'Inventory models', complete: true },
      { id: 't-9', label: 'Dynamic programming', complete: false },
      { id: 't-10', label: 'Decision analysis', complete: false },
      { id: 't-11', label: 'Simulation basics', complete: false },
      { id: 't-12', label: 'Multi-objective methods', complete: false },
      { id: 't-13', label: 'Revision and past papers', complete: false },
    ],
    materials: [
      {
        id: 'm-1',
        title: 'Week 1–4 lecture slides',
        kind: 'Slides',
        addedBy: 'Prof. N. Okonkwo',
        addedOn: '18 Jul 2026',
        size: '8.4 MB',
        source: 'lecturer',
        reviewed: true,
      },
      {
        id: 'm-2',
        title: 'Simplex worked examples',
        kind: 'Notes',
        addedBy: 'Prof. N. Okonkwo',
        addedOn: '25 Jul 2026',
        size: '2.1 MB',
        source: 'lecturer',
        reviewed: true,
      },
      {
        id: 'm-3',
        title: 'Problem set 3 — duality',
        kind: 'Problem set',
        addedBy: 'Prof. N. Okonkwo',
        addedOn: '2 Aug 2026',
        size: '640 KB',
        source: 'lecturer',
        reviewed: false,
      },
      {
        id: 'm-4',
        title: 'My tutorial notes — queueing',
        kind: 'Notes',
        addedBy: 'You',
        addedOn: '9 Aug 2026',
        size: '1.2 MB',
        source: 'student',
        reviewed: true,
      },
      {
        id: 'm-5',
        title: 'Operations Research — Hillier & Lieberman',
        kind: 'Reading',
        addedBy: 'Knowledge marketplace',
        addedOn: 'Licensed 4 Aug 2026',
        size: 'Rental · 30 days',
        source: 'lecturer',
        reviewed: false,
        marketplaceResourceId: 'kr-1',
      },
    ],
    lectures: [
      {
        id: 'l-1',
        title: 'Formulating linear programmes',
        lecturer: 'Prof. N. Okonkwo',
        recordedOn: '15 Jul 2026',
        durationMinutes: 52,
        watchedMinutes: 52,
      },
      {
        id: 'l-2',
        title: 'Simplex in practice',
        lecturer: 'Prof. N. Okonkwo',
        recordedOn: '22 Jul 2026',
        durationMinutes: 61,
        watchedMinutes: 61,
      },
      {
        id: 'l-3',
        title: 'Duality and shadow prices',
        lecturer: 'Prof. N. Okonkwo',
        recordedOn: '29 Jul 2026',
        durationMinutes: 58,
        watchedMinutes: 31,
      },
      {
        id: 'l-4',
        title: 'Queueing systems',
        lecturer: 'Prof. N. Okonkwo',
        recordedOn: '5 Aug 2026',
        durationMinutes: 55,
        watchedMinutes: 0,
      },
    ],
    quizzes: [
      {
        id: 'q-1',
        title: 'Duality and sensitivity check',
        kind: 'Quiz',
        scored: true,
        attemptsAllowed: 2,
        attemptsUsed: 1,
        bestScore: 67,
        dueLabel: 'Due Friday, 21 Aug',
        questions: [
          {
            id: 'q-1-1',
            prompt: 'In a maximisation problem, what does a shadow price represent?',
            options: [
              'The cost of adding one more decision variable',
              'The change in the objective value from one more unit of a binding resource',
              'The slack remaining in a non-binding constraint',
              'The reduced cost of a non-basic variable',
            ],
            correctIndex: 1,
            feedback:
              'A shadow price is the marginal change in the objective function per unit increase in a binding constraint.',
          },
          {
            id: 'q-1-2',
            prompt: 'A constraint with positive slack at the optimum has a shadow price of:',
            options: ['Zero', 'One', 'Its coefficient', 'Undefined'],
            correctIndex: 0,
            feedback:
              'Non-binding constraints have zero shadow price — relaxing them further changes nothing.',
          },
          {
            id: 'q-1-3',
            prompt: 'The dual of a maximisation problem with ≤ constraints is:',
            options: [
              'A maximisation with ≥ constraints',
              'A minimisation with ≥ constraints',
              'A minimisation with ≤ constraints',
              'Identical to the primal',
            ],
            correctIndex: 1,
            feedback: 'Max with ≤ becomes min with ≥, and the roles of costs and resources swap.',
          },
        ],
      },
      {
        id: 'q-2',
        title: 'Simplex method practice',
        kind: 'Practice test',
        scored: false,
        attemptsAllowed: null,
        attemptsUsed: 4,
        bestScore: null,
        questions: [
          {
            id: 'q-2-1',
            prompt: 'Which variable enters the basis in a maximisation simplex iteration?',
            options: [
              'The one with the most negative reduced cost',
              'The one with the largest right-hand side',
              'The first non-basic variable',
              'Any basic variable',
            ],
            correctIndex: 0,
            feedback: 'Choose the entering variable with the most negative reduced cost row entry.',
          },
          {
            id: 'q-2-2',
            prompt: 'The minimum ratio test determines:',
            options: [
              'Which variable enters',
              'Which variable leaves',
              'Whether the problem is unbounded',
              'The number of iterations',
            ],
            correctIndex: 1,
            feedback: 'The ratio test picks the leaving variable and keeps the solution feasible.',
          },
        ],
      },
    ],
    studyGroups: [
      {
        id: 'sg-1',
        name: 'SVG 401 — Operations Research II',
        members: 148,
        nextSession: 'Thursday, 18:00 · Engineering library',
        groupId: 'grp-1',
      },
    ],
    discussion: [
      {
        id: 'd-1',
        topic: 'Duality and sensitivity',
        author: 'Ifeoma Eze',
        handle: 'ifeoma.e',
        body: 'Does the shadow price stay valid once the right-hand side leaves the allowable range?',
        at: '2 days ago',
        replies: 4,
        resolvedBy: 'Prof. N. Okonkwo',
      },
      {
        id: 'd-2',
        topic: 'Queueing theory',
        author: 'Tolu Adeleke',
        handle: 'tolua',
        body: 'For M/M/1, is utilisation above 1 ever meaningful or is the model simply invalid there?',
        at: '5 hours ago',
        replies: 2,
      },
    ],
  },
  {
    courseCode: 'SYE 305',
    topics: [
      { id: 't-c1', label: 'System modelling', complete: true },
      { id: 't-c2', label: 'Transfer functions', complete: true },
      { id: 't-c3', label: 'Time response', complete: true },
      { id: 't-c4', label: 'Root locus', complete: false },
      { id: 't-c5', label: 'Frequency response', complete: false },
      { id: 't-c6', label: 'PID tuning', complete: false },
      { id: 't-c7', label: 'State space', complete: false },
      { id: 't-c8', label: 'Stability criteria', complete: false },
    ],
    materials: [
      {
        id: 'm-c1',
        title: 'Course outline and reading list',
        kind: 'Reading',
        addedBy: 'Dr. F. Adeyemi',
        addedOn: '14 Jul 2026',
        size: '320 KB',
        source: 'lecturer',
        reviewed: true,
      },
      {
        id: 'm-c2',
        title: 'Transfer function exercises',
        kind: 'Problem set',
        addedBy: 'Dr. F. Adeyemi',
        addedOn: '28 Jul 2026',
        size: '890 KB',
        source: 'lecturer',
        reviewed: true,
      },
      {
        id: 'm-c3',
        title: 'Root locus sketching template',
        kind: 'Template',
        addedBy: 'You',
        addedOn: '7 Aug 2026',
        size: '210 KB',
        source: 'student',
        reviewed: false,
      },
    ],
    lectures: [
      {
        id: 'l-c1',
        title: 'Modelling physical systems',
        lecturer: 'Dr. F. Adeyemi',
        recordedOn: '16 Jul 2026',
        durationMinutes: 48,
        watchedMinutes: 48,
      },
      {
        id: 'l-c2',
        title: 'Second-order time response',
        lecturer: 'Dr. F. Adeyemi',
        recordedOn: '30 Jul 2026',
        durationMinutes: 54,
        watchedMinutes: 12,
      },
    ],
    quizzes: [
      {
        id: 'q-c1',
        title: 'Time response fundamentals',
        kind: 'Quiz',
        scored: true,
        attemptsAllowed: 3,
        attemptsUsed: 0,
        bestScore: null,
        dueLabel: 'Due Monday, 24 Aug',
        questions: [
          {
            id: 'q-c1-1',
            prompt: 'Increasing the damping ratio of a second-order system will:',
            options: [
              'Increase overshoot',
              'Reduce overshoot',
              'Leave overshoot unchanged',
              'Make the system unstable',
            ],
            correctIndex: 1,
            feedback: 'Higher damping reduces overshoot, at the cost of a slower rise time.',
          },
          {
            id: 'q-c1-2',
            prompt: 'A pole on the right half of the s-plane implies:',
            options: ['Marginal stability', 'Stability', 'Instability', 'Nothing about stability'],
            correctIndex: 2,
            feedback: 'Any right-half-plane pole produces an unbounded response — the system is unstable.',
          },
        ],
      },
    ],
    studyGroups: [],
    discussion: [
      {
        id: 'd-c1',
        topic: 'Root locus',
        author: 'Daniel Uzo',
        handle: 'danielu',
        body: 'Is there a quick way to find asymptote angles without drawing the whole locus?',
        at: '1 day ago',
        replies: 1,
      },
    ],
  },
]

export function learningFor(courseCode: string) {
  return courseLearning.find((item) => item.courseCode === courseCode)
}
